import { Button, ModalBody, ModalFooter, ModalHeader, useModal } from "@homebound/beam";
import { useState } from "react";
import { FileField, FileFieldProps } from "src/components/FileField";
import { AssetUppyFile, useUppyUploader } from "src/components/uploader/UppyUploader";
import { AssetInput } from "src/generated/graphqlTypes";

type ImportFromDocumentModalProps = {
  /** Called once the document is in S3; the caller kicks off whichever import mutation it owns. */
  onImport: (asset: AssetInput) => Promise<void> | void;
  allowedFileTypes?: FileFieldProps["allowedFileTypes"];
};

/**
 * Picks the document that Blueprint AI will extract data from.
 *
 * Open it with `aiMode` so the header picks up the Blueprint AI logo and gradient title. This uploads
 * the file and hands back an `AssetInput` — calling the relevant `...AiProposal` mutation is the
 * caller's job, since each import flow has its own mutation and input.
 */
export function ImportFromDocumentModal(props: ImportFromDocumentModalProps) {
  const { onImport, allowedFileTypes } = props;
  const { closeModal } = useModal();
  const [file, setFile] = useState<File>();
  const [uploading, setUploading] = useState(false);

  async function handleUploaded(uppyFile: AssetUppyFile) {
    try {
      await onImport({
        contentType: uppyFile.type,
        fileName: uppyFile.name,
        s3Key: uppyFile.meta.s3Key,
        fileSizeInBytes: uppyFile.size ?? 0,
      });
      closeModal();
    } catch {
      // The mutation reports its own failure via the Apollo error snackbar, so just leave the modal
      // open on the chosen file and let the user try again.
    } finally {
      setUploading(false);
    }
  }

  const uppy = useUppyUploader({
    autoProceed: true,
    maxNumberOfFiles: 1,
    onFinish: (uppyFile) => void handleUploaded(uppyFile),
    onError: () => setUploading(false),
  });

  return (
    <>
      <ModalHeader>Import From Document</ModalHeader>
      <ModalBody>
        <FileField
          label="Drag & drop document"
          inputLabel="Upload Document"
          file={file}
          setFile={setFile}
          allowedFileTypes={allowedFileTypes}
        />
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="text" onClick={closeModal} />
        <Button
          label="Import"
          variant="ai"
          disabled={(!file && "Upload a document to import") || (uploading && "Uploading...")}
          onClick={() => {
            if (!file) return;
            setUploading(true);
            // Uppy only clears itself after a fully successful run, so drop any failed prior attempt
            // first — otherwise `addFile` rejects the retry as a duplicate / over `maxNumberOfFiles`.
            uppy.clear();
            uppy.addFile({ name: file.name, size: file.size, type: file.type, data: file });
          }}
        />
      </ModalFooter>
    </>
  );
}
